import clsx from 'clsx'
import { XIcon } from 'lucide-react'
import { useCallback, useEffect, useId, useState } from 'react'
import { useLocation } from 'react-router'

import { DismissibleLayer, FocusTrap, IconButton, Portal, Presence, useScrollLock } from 'slab'

function Bars3BottomLeftIcon (props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns='http://www.w3.org/2000/svg'
      fill='none'
      viewBox='0 0 24 24'
      strokeWidth='1.5'
      stroke='currentColor'
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        d='M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12'
      />
    </svg>
  )
}

interface MobileNavMenuProps {
  children?: React.ReactNode
}

export function MobileNavMenu ({ children }: MobileNavMenuProps) {
  const [open, setOpen] = useState(false)
  const location = useLocation()

  const { lockScroll, unlockScroll } = useScrollLock()

  const openSidebar = useCallback(() => {
    setOpen(true)
    lockScroll()
  }, [lockScroll])

  const closeSidebar = useCallback(() => {
    setOpen(false)
    unlockScroll()
  }, [unlockScroll])

  useEffect(() => {
    closeSidebar()
  }, [location, closeSidebar])

  const id = useId()

  return (
    <>
      <button
        aria-controls={id}
        aria-label='Open sidebar'
        aria-haspopup='dialog'
        aria-expanded={open}
        className='border-r border-gray-200 px-4 text-gray-500 outline-transparent outline-2 focus-visible:outline-thatch-500'
        onClick={openSidebar}
        type='button'
      >
        <Bars3BottomLeftIcon aria-hidden='true' className='size-6' />
      </button>
      <Portal>
        <div>
          <div
            className={clsx('fixed inset-0 bg-black transition duration-300', {
              'opacity-0 pointer-events-none delay-200': !open,
              'opacity-40': open,
            })}
          />
          <DismissibleLayer
            asChild
            onEscapeKey={closeSidebar}
            onFocusOutside={closeSidebar}
            onPointerOutside={closeSidebar}
          >
            <Presence asChild present={open}>
              <FocusTrap
                active={open}
                className={clsx(
                  'fixed left-0 top-0 bottom-0 w-full max-w-xs bg-white transition-transform duration-300',
                  {
                    'animate-in slide-in-from-left ease-out': open,
                    'animate-out slide-out-to-left ease-out fill-mode-forwards':
                    !open,
                  }
                )}
                setInitialFocusOn='a'
                id={id}
                role='dialog'
              >
                <div className='flex flex-col h-full'>
                  <div className='h-14 pl-7 pr-3 flex items-center justify-between'>
                    <div className='flex items-center'>
                      <img
                        alt='Thatch logo'
                        width='131'
                        height='160'
                        src={RAILS_ASSET_URL('logo.png')}
                        className='h-6 w-auto'
                      />
                      <img
                        className='h-9 w-auto ml-1'
                        src={RAILS_ASSET_URL('logo_all_cap.svg')}
                      />
                    </div>
                    <IconButton icon={XIcon} label='Close sidebar' onClick={closeSidebar} />
                  </div>
                  <div className='flex-1 overflow-y-auto'>
                    {children}
                  </div>
                </div>
              </FocusTrap>
            </Presence>
          </DismissibleLayer>
        </div>
      </Portal>
    </>
  )
}
